import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useModalStore } from "@/stores/modalStore";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import { AboutModal } from "@/components/ui";

interface TopNavbarProps {
  className?: string;
}

// Navigation items - moved outside component to avoid useEffect dependency warning
const navigationItems = [
  { label: "Home", path: "/map" },
  { label: "Dashboard", path: "/dashboard" },
  { label: "Asset Details", path: "/asset-management" },
  { label: "Reporting", path: "/reporting" },
  { label: "Bulk assignments", path: "/bulk-assignments" },
];

export const TopNavbar: React.FC<TopNavbarProps> = ({ className = "" }) => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const {
    isNavDropdownOpen,
    showAboutModal,
    toggleNavDropdown,
    closeNavDropdown,
    openAboutModal,
    closeAboutModal,
  } = useModalStore();

  // Storage key for remembering last active route
  const LAST_ROUTE_KEY = "taskr-last-active-route";

  // Save current route to localStorage when it changes
  useEffect(() => {
    const currentPath = location.pathname;
    const matchingItem = navigationItems.find(
      (item) =>
        item.path === currentPath ||
        (item.path === "/map" && currentPath === "/")
    );

    if (matchingItem) {
      localStorage.setItem(LAST_ROUTE_KEY, matchingItem.path);
    }
  }, [location.pathname]);

  // Get last active route from localStorage
  const getLastActiveRoute = (): string => {
    try {
      return localStorage.getItem(LAST_ROUTE_KEY) || "/map";
    } catch (error) {
      console.warn("Error reading last route from localStorage:", error);
      return "/map";
    }
  };

  const isActiveRoute = (path: string) => {
    if (path === "/map" && location.pathname === "/") return true;
    return location.pathname === path;
  };

  // Check if this is the remembered last active route
  const isLastActiveRoute = (path: string): boolean => {
    const lastRoute = getLastActiveRoute();
    return lastRoute === path;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      closeNavDropdown();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <nav
      className={`bg-white w-full relative rounded-b-xl shadow ${className}`}
    >
      <div className="flex flex-row items-center relative size-full">
        <div className="box-border content-stretch flex flex-row items-center justify-between p-2 relative size-full">
          {/* Left section - Logo and Navigation */}
          <div className="basis-0 box-border content-stretch flex flex-row gap-6 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
            {/* Logo */}
            <Link
              to="/map"
              className="bg-white box-border content-stretch flex flex-row gap-2.5 items-center justify-center px-3.5 py-px relative rounded-[14px] shrink-0 size-10 hover:bg-gray-50 transition-colors"
            >
              <div
                className="h-8 shrink-0 w-[26px] bg-no-repeat bg-center"
                style={{
                  backgroundImage: `url('images/logo-mini.svg')`,
                }}
              />
            </Link>

            {/* Navigation Items */}
            <div className="basis-0 box-border content-stretch flex flex-row grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
              {navigationItems.map((item) => {
                const isActive = isActiveRoute(item.path);
                const isLastActive = isLastActiveRoute(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`box-border content-stretch flex flex-row gap-2.5 items-center justify-center px-4 py-2.5 relative shrink-0 rounded transition-all duration-200 hover:bg-gray-50 ${
                      isActive
                        ? "text-custom-marian-blue font-semibold bg-blue-50"
                        : isLastActive
                          ? "text-custom-marian-blue font-medium bg-slate-50"
                          : "text-custom-text-gray-dark"
                    } ${
                      isActive
                        ? "border-b-2 border-blue-600"
                        : isLastActive
                          ? "border-b border-blue-300"
                          : "border-b-2 border-transparent"
                    }`}
                  >
                    <span
                      className="font-semibold leading-[1.5] text-[14px] text-left whitespace-nowrap"
                      style={{
                        fontFamily: "Open Sans",
                        fontVariationSettings: "'wdth' 100",
                      }}
                    >
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right section - User Profile */}
          <div className="relative">
            <button
              onClick={toggleNavDropdown}
              className="box-border cursor-pointer content-stretch flex flex-row gap-5 items-center justify-start pl-2 pr-4 py-2 relative rounded-xl shrink-0 border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <div className="box-border content-stretch flex flex-row gap-3 items-center justify-start p-0 relative shrink-0">
                <div className="relative shrink-0 size-[30px]">
                  <img
                    alt="User avatar"
                    className="block max-w-none size-full rounded-full"
                    height="30"
                    src={"images/userAvatar.png"} // Replace with actual user avatar URL
                    width="30"
                  />
                </div>
                <div className="box-border content-stretch flex flex-col items-start justify-start pb-0.5 pt-0 px-0 relative shrink-0">
                  <div
                    className="font-bold leading-[1.5] text-custom-text-onyx text-[14px] text-left whitespace-nowrap"
                    style={{
                      fontFamily: "Open Sans",
                      fontVariationSettings: "'wdth' 100",
                    }}
                  >
                    {user?.user_metadata?.first_name || user?.email || "User"}
                  </div>
                </div>
              </div>
              <div className="overflow-clip relative shrink-0 size-4">
                <div className="flex items-center justify-center">
                  <div className="" title="Dropdown arrow">
                    {isNavDropdownOpen ? (
                      <AiOutlineUp
                        size={20}
                        className="block max-w-zone size-full"
                      />
                    ) : (
                      <AiOutlineDown
                        size={20}
                        className="block max-w-zone size-full"
                      />
                    )}
                  </div>
                </div>
              </div>
            </button>

            {/* Dropdown Menu */}
            {isNavDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-custom-text-onyx">
                    {user?.user_metadata?.first_name}{" "}
                    {user?.user_metadata?.last_name}
                  </p>
                  <p className="text-xs text-custom-text-gray-dark">
                    {user?.email}
                  </p>
                </div>
                <div className="p-2 border-b border-gray-100">
                  <button
                    onClick={() => {
                      openAboutModal();
                      closeNavDropdown();
                    }}
                    className="w-full px-6 py-3 rounded-[10px] bg-gray-50 custom-text-onyx hover:bg-gray-200 border transition-colors font-semibold cursor-pointer leading-[1.4] text-[14px] text-center whitespace-nowrap"
                  >
                    About
                  </button>
                </div>
                <div className="p-2 border-b border-gray-100">
                  <button
                    onClick={handleSignOut}
                    className="w-full px-6 py-3 rounded-[10px] border custom-b-marian-blue text-[#444] hover:border-red-500 hover:bg-red-500 hover:text-white transition-colors font-semibold cursor-pointer leading-[1.4] text-[14px] text-center whitespace-nowrap"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {isNavDropdownOpen && (
        <div className="fixed inset-0 z-40" onClick={closeNavDropdown} />
      )}

      {/* About Modal */}
      <AboutModal
        open={showAboutModal}
        onOpenChange={(open) => !open && closeAboutModal()}
      />
    </nav>
  );
};
